/**
 * Loads the reference data the RMA processing form needs — shipping
 * types, warranty statuses, test results, relay-controller firmware
 * versions, and (staff-only) RMA / warranty fees — into the shared
 * resource-handler entity cache, fetching each only when absent.
 *
 * Mirrors the legacy RmaHOC prefetch.
 *
 * Contains:
 * - useRmaReferenceData (default export)
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchAllRelayControllerFirmwareVersions } from '@sophocles/entities/RelayControllerFirmwareVersion';
import { fetchAllRmaFees } from '@sophocles/entities/RmaFee';
import { fetchAllTestResults } from '@sophocles/entities/TestResult';
import { fetchAllWarrantyFees } from '@sophocles/entities/WarrantyFee';
import { fetchAllWarrantyStatuses } from '@sophocles/entities/WarrantyStatus';
import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import { setEntityData } from '@sophocles/features/resources/resourceHandlerActions';
import { getEntityDataSelector } from '@sophocles/features/resources/resourceHandlerSelectors';
import { loadShippingTypes } from '@sophocles/features/warehouse/orders/api';

export default function useRmaReferenceData() {
  const dispatch = useDispatch();
  const entityData = useSelector(getEntityDataSelector);
  const user = useSelector(getAuthUserSelector);

  useEffect(() => {
    const canViewFees = user.get('is_superuser', false) || user.get('is_staff', false);

    const fetchIfNeeded = (fetchFn, entity) => {
      if (!entityData.has(entity)) {
        fetchFn().then(data => dispatch(setEntityData(entity, data)));
      }
    };

    fetchIfNeeded(() => dispatch(loadShippingTypes()), 'ShippingTypes');
    fetchIfNeeded(() => dispatch(fetchAllWarrantyStatuses()), 'WarrantyStatuses');
    fetchIfNeeded(() => dispatch(fetchAllTestResults()), 'TestResults');
    fetchIfNeeded(() => dispatch(fetchAllRelayControllerFirmwareVersions()), 'RelayControllerFirmwareVersions');

    if (canViewFees) {
      fetchIfNeeded(() => dispatch(fetchAllRmaFees()), 'RmaFees');
      fetchIfNeeded(() => dispatch(fetchAllWarrantyFees()), 'WarrantyFees');
    }
  }, [dispatch, entityData, user]);
}
